import { useState, useMemo, useEffect } from "react";
import { getAccountChargesByUser, useQuery } from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import { CURRENCY } from '../shared/currency';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import AccountChargesManager from "../trading-accounts/AccountChargesManager";
import AccountFinanceDialog from "./AccountFinanceDialog";
import AccountExpenseDeleteButton from "./AccountExpenseDeleteButton";
import { Pencil } from "lucide-react";
import { normalizePeriodBounds, isWithinPeriod, accountExpensesInPeriod, chargeAmountInPeriod, countsForPayouts, countsForCosts, acquisitionDate, fundedDate, hasPropFirm } from "./pfmPeriod";
import { isPropFirmFundedStatus } from "../trading-accounts/accountStatus";

interface Payout {
  id: string;
  date: Date | string;
  amount: number;
  notes?: string | null;
  source?: string | null;
  kind?: string | null;
}

interface EnrichedAccount {
  id: string;
  name: string;
  status: string;
  challengeCost?: number | null;
  challengeCostCurrency?: string | null;
  activationFee?: number | null;
  activationFeeCurrency?: string | null;
  initialBalance: number;
  currency: string;
  createdAt?: Date | string;
  fundedAt?: Date | string | null;
  payouts: Payout[];
  propFirmAdjustments?: { id: string; date: Date | string; amount: number; grossAmount?: number | null; source?: string | null }[];
  totalPayouts: number;
  effectiveTemplate?: { name: string; color?: string; textColor?: string } | null;
}

interface AccountCharge {
  id: string;
  accountId: string | null;
  name: string;
  amount: number;
  frequency: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  billingDay?: number | null;
  createdAt?: Date | string;
}

interface Props {
  accounts: EnrichedAccount[];
  isDark: boolean;
  startDate?: Date;
  endDate?: Date;
  sections?: {
    reporting?: boolean;
    accountsTable?: boolean;
    globalCharges?: boolean;
  };
  hideTitle?: boolean;
  granularityOverride?: "month" | "quarter" | "year";
  /** Affiche le bouton d'édition des infos financières. Faux par défaut : la
   *  vue mentor (lecture seule) ne doit pas proposer d'éditer le compte d'un
   *  étudiant. */
  editable?: boolean;
}

type ReportGranularity = "month" | "quarter" | "year";

/** Charge réelle pour le tableau "Suivi par compte".
 *  Délègue à la logique de période partagée (bornes inclusives). */
function realChargeAmount(
  charge: AccountCharge,
  sinceDate: Date,
  startDate: Date | undefined,
  endDate: Date | undefined,
): number {
  const { start, end } = normalizePeriodBounds(startDate ?? null, endDate ?? null);
  return chargeAmountInPeriod(charge as any, sinceDate, start, end);
}

const MONTH_NAMES_SHORT = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];

interface PeriodRow {
  key: string;
  label: string;
  payouts: number;
  challengeCost: number;
  activationFee: number;
  linkedCharges: number;
  globalCharges: number;
  totalCost: number;
  net: number;
  payoutsCount: number;
}

export default function PayoutsAnalyticsSection({ accounts, isDark, startDate, endDate, sections, hideTitle, granularityOverride, editable = false }: Props) {
  const showReporting = sections?.reporting ?? true;
  const showAccountsTable = sections?.accountsTable ?? true;
  const showGlobalCharges = sections?.globalCharges ?? true;
  const t = useTranslation();
  const { data: chargesData, refetch: refetchCharges } = useQuery(getAccountChargesByUser);
  const charges: AccountCharge[] = (chargesData ?? []) as AccountCharge[];
  const [granularityInternal, setGranularity] = useState<ReportGranularity>("month");
  const granularity = granularityOverride ?? granularityInternal;
  const PAGE_SIZE = 10;
  const [accountPage, setAccountPage] = useState(0);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  // Relu dans `accounts` à chaque rendu plutôt que figé à l'ouverture : un
  // payout supprimé depuis la modale disparaît aussitôt de sa liste.
  const editingAccount = editingAccountId ? accounts.find((a) => a.id === editingAccountId) ?? null : null;

  // Périmètre partagé avec les KPIs du haut de page (pfmPeriod.ts) : les deux
  // vues doivent répondre le même chiffre sur la même période.
  const eligibleAccounts = useMemo(() => accounts.filter(countsForPayouts), [accounts]);

  const allAccountsForTable = useMemo(() => accounts.filter(countsForCosts), [accounts]);

  const globalCharges = useMemo(
    () => charges.filter((c) => c.accountId === null),
    [charges]
  );

  // Bornes inclusives (fin de journée comprise) partagées par tous les totaux ci-dessous.
  const periodBounds = useMemo(() => normalizePeriodBounds(startDate, endDate), [startDate, endDate]);

  // Payouts filtrés par période
  const filteredPayouts = useMemo(() =>
    eligibleAccounts
      .flatMap((a) =>
        a.payouts
          .filter((p) => isWithinPeriod(p.date, periodBounds.start, periodBounds.end))
          .map((p) => ({ ...p, accountName: a.name, currency: a.currency, accountId: a.id }))
      ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [eligibleAccounts, periodBounds]
  );

  const totalPayouts = useMemo(() => filteredPayouts.reduce((s, p) => s + p.amount, 0), [filteredPayouts]);

  // Les frais ponctuels sont datés par leur événement (création / passage funded)
  // pour rester cohérents avec la période sélectionnée et le récap du mois.
  const totalChallengeCost = useMemo(
    () => allAccountsForTable.reduce(
      (sum, a) => sum + accountExpensesInPeriod(
        { ...a, activationFee: 0 } as any,
        periodBounds.start,
        periodBounds.end,
      ),
      0,
    ),
    [allAccountsForTable, periodBounds]
  );

  const totalActivationFee = useMemo(
    () => allAccountsForTable.reduce(
      (sum, a) => sum + accountExpensesInPeriod(
        { ...a, challengeCost: 0 } as any,
        periodBounds.start,
        periodBounds.end,
      ),
      0,
    ),
    [allAccountsForTable, periodBounds]
  );

  const totalLinkedChargesCost = useMemo(() =>
    allAccountsForTable
      .reduce((sum, account) => {
        const sinceDate = account.createdAt ? new Date(account.createdAt) : new Date();
        const linked = charges.filter((c) => c.accountId === account.id);
        return sum + linked.reduce((s, c) => s + realChargeAmount(c, sinceDate, startDate, endDate), 0);
      }, 0),
    [allAccountsForTable, charges, startDate, endDate]
  );

  const totalGlobalChargesCost = useMemo(() => {
    if (globalCharges.length === 0) return 0;
    const dates = allAccountsForTable
      .map((a) => a.createdAt ? new Date(a.createdAt).getTime() : Date.now())
      .sort((a, b) => a - b);
    const sinceDate = dates.length > 0 ? new Date(dates[0]) : new Date();
    return globalCharges.reduce((sum, c) => sum + realChargeAmount(c, sinceDate, startDate, endDate), 0);
  }, [globalCharges, allAccountsForTable, startDate, endDate]);

  const totalCost = totalChallengeCost + totalActivationFee + totalLinkedChargesCost + totalGlobalChargesCost;
  const netResult = totalPayouts - totalCost;
  const roi = totalCost > 0 ? (netResult / totalCost) * 100 : null;

  const bestPayout = useMemo(() => {
    return filteredPayouts.length > 0 ? Math.max(...filteredPayouts.map((p) => p.amount)) : null;
  }, [filteredPayouts]);

  // ── Helpers de période (hoistés pour usage dans le JSX tooltip) ──────────
  const getKey = (date: Date): string => {
    const y = date.getFullYear();
    const m = date.getMonth();
    if (granularity === "month") return `${y}-${String(m + 1).padStart(2, "0")}`;
    if (granularity === "quarter") return `${y}-Q${Math.floor(m / 3) + 1}`;
    return `${y}`;
  };

  const getLabel = (key: string): string => {
    if (granularity === "month") {
      const [y, mo] = key.split("-");
      return `${MONTH_NAMES_SHORT[parseInt(mo, 10) - 1]} ${y}`;
    }
    if (granularity === "quarter") return key.replace("-", " ");
    return key;
  };

  const getPeriodBounds = (key: string): { start: Date; end: Date } => {
    if (granularity === "year") {
      const y = parseInt(key, 10);
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    }
    if (granularity === "quarter") {
      const [y, q] = key.split("-Q");
      const startMonth = (parseInt(q, 10) - 1) * 3;
      return {
        start: new Date(parseInt(y, 10), startMonth, 1),
        end: new Date(parseInt(y, 10), startMonth + 3, 0, 23, 59, 59, 999),
      };
    }
    // month
    const [y, mo] = key.split("-");
    const yr = parseInt(y, 10);
    const mn = parseInt(mo, 10) - 1;
    return { start: new Date(yr, mn, 1), end: new Date(yr, mn + 1, 0, 23, 59, 59, 999) };
  };

  // ── Tableau de reporting par période ──────────────────────────────────────
  const reportRows = useMemo((): PeriodRow[] => {
    // Collecter toutes les dates couvertes par les payouts
    const periodKeys = new Set<string>();

    // Collecter les clés depuis les payouts
    filteredPayouts.forEach((p) => periodKeys.add(getKey(new Date(p.date))));

    // Collecter les clés depuis les dates d'ÉVÉNEMENT des frais ponctuels : un
    // compte acheté en juin et saisi en juillet doit créer la ligne de juin,
    // sinon son challengeCost n'aurait aucune ligne où s'afficher.
    allAccountsForTable.forEach((a) => {
      const boughtAt = acquisitionDate(a as any);
      if (boughtAt) periodKeys.add(getKey(boughtAt));
      const funded = fundedDate(a as any);
      if (funded) periodKeys.add(getKey(funded));
    });

    // Idem pour les charges : une charge globale peut tomber dans un mois sans
    // aucun payout ni achat de compte.
    charges.forEach((c) => {
      const d = c.startDate ? new Date(c.startDate) : c.createdAt ? new Date(c.createdAt) : null;
      if (d) periodKeys.add(getKey(d));
    });

    // Si aucune période trouvée, ajouter la période courante
    if (periodKeys.size === 0) {
      periodKeys.add(getKey(new Date()));
    }

    const rows: PeriodRow[] = [];

    for (const key of Array.from(periodKeys).sort()) {
      const { start, end } = getPeriodBounds(key);

      // Payouts dans cette période
      const periodPayouts = filteredPayouts.filter((p) => {
        const d = new Date(p.date);
        return d >= start && d <= end;
      });
      const payoutsTotal = periodPayouts.reduce((s, p) => s + p.amount, 0);

      // Frais ponctuels : datés par leur événement réel via la logique partagée.
      // Le challengeCost suit purchasedAt (date d'achat réelle) et non createdAt,
      // qui n'est que l'horodatage de saisie d'un compte ajouté a posteriori.
      const challengeCost = allAccountsForTable.reduce(
        (sum, a) => sum + accountExpensesInPeriod({ ...a, activationFee: 0 } as any, start, end),
        0,
      );

      const activationFeeForPeriod = allAccountsForTable.reduce(
        (sum, a) => sum + accountExpensesInPeriod({ ...a, challengeCost: 0 } as any, start, end),
        0,
      );

      // Charges liées, bornées à cette période par la logique partagée
      const linkedCharges = allAccountsForTable.reduce((sum, account) => {
        const sinceDate = account.createdAt ? new Date(account.createdAt) : start;
        const linked = charges.filter((c) => c.accountId === account.id);
        return sum + linked.reduce(
          (s, c) => s + chargeAmountInPeriod(c as any, sinceDate, start, end),
          0,
        );
      }, 0);

      // Charges globales, bornées à cette période
      const globalChargesTotalForPeriod = globalCharges.reduce(
        (sum, c) => sum + chargeAmountInPeriod(c as any, start, start, end),
        0,
      );

      const totalCostForPeriod = challengeCost + activationFeeForPeriod + linkedCharges + globalChargesTotalForPeriod;
      const net = payoutsTotal - totalCostForPeriod;

      rows.push({
        key,
        label: getLabel(key),
        payouts: payoutsTotal,
        challengeCost,
        activationFee: activationFeeForPeriod,
        linkedCharges,
        globalCharges: globalChargesTotalForPeriod,
        totalCost: totalCostForPeriod,
        net,
        payoutsCount: periodPayouts.length,
      });
    }

    return rows;
  }, [filteredPayouts, allAccountsForTable, charges, globalCharges, granularity, startDate, endDate]);

  const reportTotals = useMemo(() => {
    if (reportRows.length === 0) return null;
    return {
      payouts: reportRows.reduce((s, r) => s + r.payouts, 0),
      challengeCost: reportRows.reduce((s, r) => s + r.challengeCost, 0),
      activationFee: reportRows.reduce((s, r) => s + r.activationFee, 0),
      linkedCharges: reportRows.reduce((s, r) => s + r.linkedCharges, 0),
      globalCharges: reportRows.reduce((s, r) => s + r.globalCharges, 0),
      totalCost: reportRows.reduce((s, r) => s + r.totalCost, 0),
      net: reportRows.reduce((s, r) => s + r.net, 0),
      payoutsCount: reportRows.reduce((s, r) => s + r.payoutsCount, 0),
    };
  }, [reportRows]);

  useEffect(() => {
    setAccountPage(0);
  }, [allAccountsForTable]);

  const pagedAccounts = useMemo(
    () => allAccountsForTable.slice(accountPage * PAGE_SIZE, (accountPage + 1) * PAGE_SIZE),
    [allAccountsForTable, accountPage]
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const formatCurrencyFull = (amount: number) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: CURRENCY, minimumFractionDigits: 2 }).format(amount);

  if (accounts.length === 0 && charges.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {t.analytics.payoutsNoEligibleAccounts}
      </div>
    );
  }

  // Couleurs theme-aware (tokens) — partagées par tous les tableaux propfirm pour un rendu uniforme
  const textSecondary = "text-muted-foreground";
  const border = "border-border";
  const headTh = "text-muted-foreground border-b border-border";
  const btnBase = "px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors";
  const btnActive = "bg-primary/15 text-primary border border-primary/40";
  const btnInactive = "text-muted-foreground hover:bg-muted/40 border border-transparent";

  const granOptions: { value: ReportGranularity; label: string }[] = [
    { value: "month", label: t.analytics.reportGranMonth },
    { value: "quarter", label: t.analytics.reportGranQuarter },
    { value: "year", label: t.analytics.reportGranYear },
  ];

  return (
    <div className="space-y-6">

      {/* ── Tableau de reporting rentabilité ── */}
      {showReporting && <div>
        {!hideTitle && <div className="flex items-center justify-between mb-3">
          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {t.analytics.reportTitle}
          </p>
          <div className="flex items-center gap-1">
            {granOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setGranularity(opt.value)}
                className={`${btnBase} ${granularity === opt.value ? btnActive : btnInactive}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>}

        {reportRows.length === 0 ? (
          <p className={`text-sm ${textSecondary} py-4 text-center`}>{t.analytics.reportNoData}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={headTh}>
                  {[
                    t.analytics.reportColPeriod,
                    t.analytics.reportColPayouts,
                    t.analytics.reportColChallengeCost,
                    t.analytics.reportColActivationFee,
                    t.analytics.reportColLinkedCharges,
                    t.analytics.reportColGlobalCharges,
                    t.analytics.reportColTotalCost,
                    t.analytics.reportColNet,
                    "ROI",
                  ].map((col, i) => {
                    const align = i === 0 ? "text-left" : "text-right";
                    return (
                      <th
                        key={i}
                        className={`${align} pb-2 pr-3 font-medium whitespace-nowrap`}
                      >
                        {col}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => {
                  const rowRoi = row.totalCost > 0 ? (row.net / row.totalCost) * 100 : null;
                  return (
                    <tr key={row.key} className={`border-b ${border}`}>
                      <td className={`py-2.5 pr-3 font-medium ${isDark ? "text-gray-100" : "text-gray-800"} whitespace-nowrap`}>
                        {row.label}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${row.payouts > 0 ? "text-pnlPositive" : textSecondary}`}>
                        {row.payouts > 0 ? `+${formatCurrencyFull(row.payouts)}` : "—"}
                        {row.payoutsCount > 0 && (
                          <span className={`ml-1 text-[0.6rem] ${textSecondary}`}>×{row.payoutsCount}</span>
                        )}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono ${row.challengeCost > 0 ? "text-pnlNegative" : textSecondary}`}>
                        {row.challengeCost > 0 ? `-${formatCurrencyFull(row.challengeCost)}` : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono ${row.activationFee > 0 ? "text-pnlNegative" : textSecondary}`}>
                        {row.activationFee > 0 ? `-${formatCurrencyFull(row.activationFee)}` : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono ${row.linkedCharges > 0 ? "text-pnlNegative" : textSecondary}`}>
                        {row.linkedCharges > 0.01 ? `-${formatCurrencyFull(row.linkedCharges)}` : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono ${row.globalCharges > 0 ? "text-pnlNegative" : textSecondary}`}>
                        {row.globalCharges > 0.01 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="underline decoration-dotted cursor-default">
                                  -{formatCurrencyFull(row.globalCharges)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs max-w-[220px]">
                                {globalCharges.map((c) => {
                                  const rowPeriod = getPeriodBounds(row.key);
                                  // Même calcul que la colonne : le détail doit sommer au total affiché.
                                  const amount = chargeAmountInPeriod(
                                    c as any,
                                    rowPeriod.start,
                                    rowPeriod.start,
                                    rowPeriod.end,
                                  );
                                  if (amount < 0.01) return null;
                                  return (
                                    <div key={c.id} className="flex justify-between gap-3">
                                      <span>{c.name}</span>
                                      <span className="font-mono text-pnlNegative">-{formatCurrencyFull(amount)}</span>
                                    </div>
                                  );
                                })}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono text-pnlNegative font-semibold`}>
                        {row.totalCost > 0.01 ? `-${formatCurrencyFull(row.totalCost)}` : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono font-bold ${row.net >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                        {row.net >= 0 ? "+" : ""}{formatCurrencyFull(row.net)}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-mono ${rowRoi === null ? textSecondary : rowRoi >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                        {rowRoi !== null ? `${rowRoi >= 0 ? "+" : ""}${rowRoi.toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {reportTotals && (
                <tfoot>
                  <tr className={`border-t-2 ${border}`}>
                    <td className={`py-2.5 pr-3 font-bold ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                      {t.analytics.reportTotalRow}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-bold ${reportTotals.payouts > 0 ? "text-pnlPositive" : textSecondary}`}>
                      {reportTotals.payouts > 0 ? `+${formatCurrencyFull(reportTotals.payouts)}` : "—"}
                      {reportTotals.payoutsCount > 0 && (
                        <span className={`ml-1 text-[0.6rem] ${textSecondary}`}>×{reportTotals.payoutsCount}</span>
                      )}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${reportTotals.challengeCost > 0 ? "text-pnlNegative" : textSecondary}`}>
                      {reportTotals.challengeCost > 0 ? `-${formatCurrencyFull(reportTotals.challengeCost)}` : "—"}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${reportTotals.activationFee > 0 ? "text-pnlNegative" : textSecondary}`}>
                      {reportTotals.activationFee > 0 ? `-${formatCurrencyFull(reportTotals.activationFee)}` : "—"}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${reportTotals.linkedCharges > 0 ? "text-pnlNegative" : textSecondary}`}>
                      {reportTotals.linkedCharges > 0.01 ? `-${formatCurrencyFull(reportTotals.linkedCharges)}` : "—"}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${reportTotals.globalCharges > 0 ? "text-pnlNegative" : textSecondary}`}>
                      {reportTotals.globalCharges > 0.01 ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="underline decoration-dotted cursor-default">
                                -{formatCurrencyFull(reportTotals.globalCharges)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs max-w-[220px]">
                              {globalCharges.map((c) => {
                                const amount = realChargeAmount(c, new Date(0), startDate, endDate);
                                if (amount < 0.01) return null;
                                return (
                                  <div key={c.id} className="flex justify-between gap-3">
                                    <span>{c.name}</span>
                                    <span className="font-mono text-pnlNegative">-{formatCurrencyFull(amount)}</span>
                                  </div>
                                );
                              })}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : "—"}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-bold text-pnlNegative`}>
                      {reportTotals.totalCost > 0.01 ? `-${formatCurrencyFull(reportTotals.totalCost)}` : "—"}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-bold ${reportTotals.net >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {reportTotals.net >= 0 ? "+" : ""}{formatCurrencyFull(reportTotals.net)}
                    </td>
                    <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${roi === null ? textSecondary : roi >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {roi !== null ? `${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>}

      {/* ── Suivi par compte ── */}
      {showAccountsTable && <div>
        {!hideTitle && <p className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
          {t.analytics.payoutsPerAccount}
        </p>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={headTh}>
                <th className="text-left pb-2 pr-3 font-medium">{t.analytics.payoutsAccountName}</th>
                <th className="text-right pb-2 pr-3 font-medium">{t.analytics.payoutsChallengeCost}</th>
                <th className="text-right pb-2 pr-3 font-medium">{t.analytics.payoutsActivationFee}</th>
                <th className="text-right pb-2 pr-3 font-medium">{t.analytics.payoutsLinkedCharges}</th>
                <th className="text-right pb-2 pr-3 font-medium">{t.analytics.payoutsReceived}</th>
                <th className="text-right pb-2 font-medium">{t.analytics.payoutsNetResult}</th>
                {editable && <th className="pb-2 w-14"><span className="sr-only">{t.analytics.payoutsEditAction}</span></th>}
              </tr>
            </thead>
            <tbody>
              {pagedAccounts.map((account) => {
                const sinceDate = account.createdAt ? new Date(account.createdAt) : new Date();
                const linkedChargesList = charges.filter((c) => c.accountId === account.id);
                const linkedCost = linkedChargesList.reduce((s, c) => s + realChargeAmount(c, sinceDate, startDate, endDate), 0);
                const challengeCost = account.challengeCost ?? 0;
                const received = account.payouts
                  .filter((p) => {
                    const d = new Date(p.date);
                    if (startDate && d < startDate) return false;
                    if (endDate && d > endDate) return false;
                    return true;
                  })
                  .reduce((s, p) => s + p.amount, 0);
                const accountActivationFee = account.activationFee ?? 0;
                const net = received - challengeCost - accountActivationFee - linkedCost;
                const dotColor = isPropFirmFundedStatus(account.status, hasPropFirm(account)) ? "bg-primary" : account.status === "challenge" ? "bg-amber-400" : "bg-gray-400";
                return (
                  <tr key={account.id} className={`border-b ${border}`}>
                    <td className="py-2.5 pr-3 font-medium">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
                        <span className="truncate max-w-[100px]">{account.name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {challengeCost > 0
                        ? <span className="text-pnlNegative">-{formatCurrencyFull(challengeCost)}</span>
                        : <span className={textSecondary}>—</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {(account.activationFee ?? 0) > 0
                        ? <span className="text-pnlNegative">-{formatCurrencyFull(account.activationFee!)}</span>
                        : <span className={textSecondary}>—</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {linkedCost > 0.01
                        ? <span className="text-pnlNegative">-{formatCurrencyFull(linkedCost)}</span>
                        : <span className={textSecondary}>—</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono font-semibold">
                      {received > 0
                        ? <span className="text-pnlPositive">+{formatCurrencyFull(received)}</span>
                        : <span className={textSecondary}>—</span>}
                    </td>
                    <td className={`py-2.5 text-right font-bold font-mono ${net >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {net >= 0 ? "+" : ""}{formatCurrencyFull(net)}
                    </td>
                    {editable && (
                      <td className="py-2.5 pl-2 text-right">
                        <div className="inline-flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            onClick={() => setEditingAccountId(account.id)}
                            className={`inline-flex h-6 w-6 items-center justify-center rounded transition-colors ${isDark ? "text-gray-400 hover:bg-gray-700 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                            aria-label={`${t.analytics.payoutsEditAction} (${account.name})`}
                            title={t.analytics.payoutsEditAction}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <AccountExpenseDeleteButton account={account} isDark={isDark} />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {allAccountsForTable.length === 0 && (
                <tr>
                  <td colSpan={editable ? 7 : 6} className={`py-4 text-center text-xs ${textSecondary}`}>
                    {t.analytics.payoutsNoData}
                  </td>
                </tr>
              )}
              {allAccountsForTable.length > PAGE_SIZE && (
                <tr>
                  <td colSpan={editable ? 7 : 6} className="py-2">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setAccountPage((p) => Math.max(0, p - 1))}
                        disabled={accountPage === 0}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors
                          ${isDark
                            ? "bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          }`}
                        aria-label={t.analytics.payoutsPaginationPrev}
                      >
                        ‹
                      </button>
                      <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {t.analytics.payoutsPaginationPage}{" "}
                        {accountPage + 1}{" "}
                        {t.analytics.payoutsPaginationOf}{" "}
                        {Math.ceil(allAccountsForTable.length / PAGE_SIZE)}
                      </span>
                      <button
                        onClick={() => setAccountPage((p) => Math.min(Math.ceil(allAccountsForTable.length / PAGE_SIZE) - 1, p + 1))}
                        disabled={(accountPage + 1) * PAGE_SIZE >= allAccountsForTable.length}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors
                          ${isDark
                            ? "bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          }`}
                        aria-label={t.analytics.payoutsPaginationNext}
                      >
                        ›
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {/* Total row */}
              {allAccountsForTable.length > 0 && (() => {
                const rowChallenge = allAccountsForTable.reduce((s, a) => s + (a.challengeCost ?? 0), 0);
                const rowActivation = allAccountsForTable.reduce((s, a) => s + (a.activationFee ?? 0), 0);
                const rowLinked = allAccountsForTable.reduce((sum, account) => {
                  const sinceDate = account.createdAt ? new Date(account.createdAt) : new Date();
                  const linked = charges.filter((c) => c.accountId === account.id);
                  return sum + linked.reduce((s, c) => s + realChargeAmount(c, sinceDate, startDate, endDate), 0);
                }, 0);
                const rowReceived = allAccountsForTable.reduce((s, a) =>
                  s + a.payouts
                    .filter((p) => {
                      const d = new Date(p.date);
                      if (startDate && d < startDate) return false;
                      if (endDate && d > endDate) return false;
                      return true;
                    })
                    .reduce((ps, p) => ps + p.amount, 0), 0);
                const rowNet = rowReceived - rowChallenge - rowActivation - rowLinked - totalGlobalChargesCost;

                const hasAnything = rowChallenge > 0 || rowActivation > 0 || rowLinked > 0 || rowReceived > 0 || totalGlobalChargesCost > 0;
                if (!hasAnything) return null;

                return (
                  <tr className={`${isDark ? "text-gray-300" : "text-gray-700"} font-semibold`}>
                    <td className="pt-3 pr-3">{t.analytics.reportTotalRow}</td>
                    <td className="pt-3 pr-3 text-right font-mono text-pnlNegative">
                      {rowChallenge > 0 ? `-${formatCurrencyFull(rowChallenge)}` : "—"}
                    </td>
                    <td className="pt-3 pr-3 text-right font-mono text-pnlNegative">
                      {rowActivation > 0 ? `-${formatCurrencyFull(rowActivation)}` : "—"}
                    </td>
                    <td className="pt-3 pr-3 text-right font-mono text-pnlNegative">
                      {(rowLinked + totalGlobalChargesCost) > 0.01
                        ? `-${formatCurrencyFull(rowLinked + totalGlobalChargesCost)}`
                        : "—"}
                    </td>
                    <td className="pt-3 pr-3 text-right font-mono text-pnlPositive">
                      {rowReceived > 0 ? `+${formatCurrencyFull(rowReceived)}` : "—"}
                    </td>
                    <td className={`pt-3 text-right font-bold font-mono ${rowNet >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>
                      {rowNet >= 0 ? "+" : ""}{formatCurrencyFull(rowNet)}
                    </td>
                    {editable && <td />}
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>}

      {editable && (
        <AccountFinanceDialog
          account={editingAccount}
          charges={editingAccount ? charges.filter((c) => c.accountId === editingAccount.id) : []}
          onClose={() => setEditingAccountId(null)}
          onChargesMutated={() => { refetchCharges(); }}
        />
      )}

      {/* ── Charges globales ── */}
      {showGlobalCharges && <div>
        <p className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
          {t.analytics.payoutsGlobalCharges}
        </p>
        <AccountChargesManager
          charges={globalCharges}
          accountId={null}
          onMutated={() => { refetchCharges(); }}
        />
      </div>}

    </div>
  );
}
